import { ITEMS_PER_PAGE } from '@/constants';
import i18n from '@/i18n';
import catalogService from '@/services/catalog';
import type { CatalogItem, CatalogRequest } from '@/types';
import { readPageSize, storePageSize } from '@/utils/pageSize';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useAuthStore } from './auth';
import { useToastStore } from './toast';

export const useCatalogsStore = defineStore(
    'catalogs',
    () => {
        // State
        const items = ref<CatalogItem[]>([]);
        const isLoading = ref(false);
        const currentPage = ref(1);
        const total = ref(0);
        const perPage = ref(ITEMS_PER_PAGE);
        const search = ref('');
        let latestListRequest = 0;

        // Computed
        const count = computed(() => items.value.length);
        const totalPages = computed(() => Math.ceil(total.value / perPage.value));

        // Actions
        const fetchItems = async (searchTerm?: string): Promise<void> => {
            const requestId = ++latestListRequest;
            const requestedSearch = searchTerm ?? search.value;
            const requestedPage = currentPage.value;
            const isCurrentRequest = () =>
                requestId === latestListRequest &&
                (searchTerm !== undefined || requestedSearch === search.value) &&
                requestedPage === currentPage.value;
            isLoading.value = true;
            try {
                const response = await catalogService.getItems({
                    company_id: useAuthStore().activeCompany?.id ?? undefined,
                    search: requestedSearch,
                    page: requestedPage,
                    per_page: readPageSize('catalogs'),
                });

                if (!isCurrentRequest()) {
                    return;
                }
                const responseData = response.data;
                items.value = responseData.data;
                currentPage.value = responseData.meta.current_page;
                total.value = responseData.meta.total;
                perPage.value = responseData.meta.per_page;
            } catch (error) {
                if (!isCurrentRequest()) {
                    return;
                }
                useToastStore().error(i18n.global.t('error.generic'));
                throw error;
            } finally {
                if (requestId === latestListRequest) {
                    isLoading.value = false;
                }
            }
        };

        const createItem = async (data: CatalogRequest): Promise<CatalogItem> => {
            isLoading.value = true;
            try {
                const response = await catalogService.store(data);
                const newItem = response.data.data;

                // Refresh list
                await fetchItems();

                return newItem;
            } catch (error) {
                useToastStore().error(i18n.global.t('common.error'));
                throw error;
            } finally {
                isLoading.value = false;
            }
        };

        const updateItem = async (id: string, data: Partial<CatalogRequest>): Promise<CatalogItem> => {
            isLoading.value = true;
            try {
                const response = await catalogService.update(id, data);
                const updatedItem = response.data.data;

                // Refresh list
                await fetchItems();

                return updatedItem;
            } catch (error) {
                useToastStore().error(i18n.global.t('common.error'));
                throw error;
            } finally {
                isLoading.value = false;
            }
        };

        const deleteItem = async (id: string): Promise<void> => {
            isLoading.value = true;
            try {
                await catalogService.delete(id);

                // Refresh list
                await fetchItems();
            } catch (error) {
                useToastStore().error(i18n.global.t('common.error'));
                throw error;
            } finally {
                isLoading.value = false;
            }
        };

        const setSearch = (term: string): void => {
            search.value = term;
            currentPage.value = 1; // Reset to first page on new search
        };

        const setPage = (page: number): void => {
            currentPage.value = page;
        };

        /** Switches the list to another page size, remembers it and reloads from page 1. */
        const setPerPage = async (size: number): Promise<void> => {
            if (!storePageSize('catalogs', size)) return;
            perPage.value = size;
            currentPage.value = 1;
            await fetchItems();
        };

        const reset = (): void => {
            items.value = [];
            currentPage.value = 1;
            total.value = 0;
            search.value = '';
            isLoading.value = false;
        };

        return {
            // State
            items,
            isLoading,
            currentPage,
            total,
            perPage,
            search,

            // Computed
            count,
            totalPages,

            // Actions
            fetchItems,
            createItem,
            updateItem,
            deleteItem,
            setSearch,
            setPage,
            setPerPage,
            reset,
        };
    },
    {
        persist: {
            key: 'app.catalogs',
            pick: ['search'],
        },
    },
);
